import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider, useAuth } from '@/auth/AuthProvider.jsx'
import { LoginPage } from '@/auth/LoginPage.jsx'
import { ForgotPasswordPage } from '@/auth/ForgotPasswordPage.jsx'
import { ResetPasswordPage } from '@/auth/ResetPasswordPage.jsx'
import { AcceptInvitePage } from '@/auth/AcceptInvitePage.jsx'
import { TenantProvider, useTenant } from '@/app/TenantProvider.jsx'
import { AppShell } from '@/app/AppShell.jsx'
import { PipelineBoard } from '@/screens/PipelineBoard.jsx'
import { JobDetail } from '@/screens/JobDetail.jsx'
import { ClientList } from '@/screens/ClientList.jsx'
import { ClientDetail } from '@/screens/ClientDetail.jsx'
import { EstimateEditor } from '@/screens/EstimateEditor.jsx'
import { InvoiceList } from '@/screens/InvoiceList.jsx'
import { InvoiceDetail } from '@/screens/InvoiceDetail.jsx'
import { ScheduleList } from '@/screens/ScheduleList.jsx'
import { Settings } from '@/screens/Settings.jsx'
import {
  OperationsDataExport,
  OperationsLayout,
  OperationsOverview,
  OperationsPayroll,
  OperationsReports,
  OperationsTemplates
} from '@/screens/Operations.jsx'
import { AdminConsole } from '@/screens/AdminConsole.jsx'
import { ApiError } from '@/lib/api.js'
import { Spinner } from '@/components/ui/feedback.jsx'

/**
 * Application root (Phase 1b, session 1).
 *
 * Provider order is deliberate and not interchangeable:
 *   QueryClient → Auth → Router → (authenticated) Tenant → Shell
 *
 * Auth sits above the Router because a session is not route-dependent, and it
 * needs the query client to clear the cache on sign-out. Tenant sits INSIDE the
 * authenticated branch because GET /api/me/features cannot be called without a
 * token — hoisting it would fire an unauthenticated request on every visit to
 * the login page.
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // The loop is read-heavy and mostly single-operator. Refetching on every
      // window focus would put a contractor's phone through a request storm
      // each time they switch back from a text message.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: (count, err) => {
        // A 4xx is an answer, not a failure to reach the server. Retrying it
        // wastes the user's time and, for a 429, makes the situation worse.
        if (err instanceof ApiError && err.status >= 400 && err.status < 500) return false
        return count < 2
      }
    },
    // Mutations never auto-retry: POST /estimates/:id/send is not safe to
    // repeat, and a duplicate estimate emailed to a homeowner is a real
    // problem rather than a cosmetic one.
    mutations: { retry: false }
  }
})

export default function App () {
  return (
    <QueryClientProvider client={queryClient}>
      {/* Opt into the v7 behaviours now. Both are no-ops for this app's routing
          and they keep the console free of upgrade warnings — noise there is
          how a real warning gets scrolled past during a build session. */}
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/* Phase 2 item 18a — both unauthenticated, both talk to Supabase
                Auth directly (no token exists yet), same posture as /login. */}
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route
              path="/admin/*"
              element={
                <RequireAuth>
                  <AdminConsole />
                </RequireAuth>
              }
            />
            <Route
              element={
                <RequireAuth>
                  <TenantProvider>
                    <AppShell />
                  </TenantProvider>
                </RequireAuth>
              }
            >
              <Route index element={<PipelineBoard />} />

              <Route path="jobs/:id" element={<JobDetail />} />
              {/* A new estimate is created in the context of its job, because
                  estimates.job_id is NOT NULL (Phase 1 D1) — there is no such
                  thing as an estimate without a job to hang it off. */}
              <Route path="jobs/:jobId/estimates/new" element={<EstimateEditor />} />
              <Route path="estimates/:id" element={<EstimateEditor />} />

              <Route path="clients" element={<ClientList />} />
              <Route path="clients/:id" element={<ClientDetail />} />

              <Route path="schedule" element={<ScheduleList />} />

              <Route path="invoices" element={<InvoiceList />} />
              <Route path="invoices/:id" element={<InvoiceDetail />} />

              <Route path="settings" element={<Settings />} />

              {/* ---- the Operations zone (Phase 9 D9.3) ------------------
                  `/` above stays the Pipeline board and the landing route,
                  for every user, whether or not they hold this grant. That
                  is the rule this whole restructure is built around: the CRM
                  is still the product and Operations is the back office. */}
              <Route
                path="operations"
                element={<RequireOperations><OperationsLayout /></RequireOperations>}
              >
                <Route index element={<OperationsOverview />} />
                <Route path="reports" element={<OperationsReports />} />
                <Route path="templates" element={<OperationsTemplates />} />
                <Route path="payroll" element={<OperationsPayroll />} />
                <Route path="data-export" element={<OperationsDataExport />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}

/**
 * Gate every authenticated route on a restored session.
 *
 * Waiting on `initialising` is what stops a reload from bouncing a signed-in
 * user to /login for the moment before localStorage is read — a flash that
 * reads as being randomly logged out.
 */
function RequireAuth ({ children }) {
  const { session, initialising } = useAuth()
  const location = useLocation()

  if (initialising) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-canvas">
        <Spinner label="Loading" />
      </div>
    )
  }

  if (!session) {
    // Remember the destination so a bookmarked link survives signing in.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />
  }

  return children
}

/**
 * Keep a user without the Operations grant out of `/operations/*` (Phase 9).
 *
 * ── THIS IS COURTESY, NOT THE BOUNDARY, AND THAT IS DELIBERATE ──────────────
 * D9.3: "API routes serving Operations data must be server-gated. Hiding the
 * nav is courtesy, not security." Every call these screens make is refused
 * server-side with 403 `operations_access_required` whatever this component
 * does. What this buys is that a member who types the URL, follows a stale
 * bookmark, or has their access revoked mid-session lands somewhere that works
 * instead of on four screens of failed requests.
 *
 * REDIRECTS TO `/` RATHER THAN RENDERING A REFUSAL, on purpose: Pipeline is
 * the default route for every user, and a sales hire who never had this access
 * should not be shown a locked door telling them what they are not trusted
 * with. `replace` so Back does not bounce them straight into it again.
 */
function RequireOperations ({ children }) {
  const { canAccessOperations } = useTenant()
  if (!canAccessOperations) return <Navigate to="/" replace />
  return children
}
